import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Form, Input, InputNumber, Modal, Popconfirm, Select, Space, Table, Tag, Typography, message } from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import tmAdminService from '../../services/tmService';
import { ok, toastErr } from './common';

const { Text } = Typography;

/** 服务分组：提交主机名 + 节点池（IP 信誉按组隔离） */
const Members = ({ group, nodes, onChanged }) => {
    const [nodeId, setNodeId] = useState(null);
    const [weight, setWeight] = useState(100);

    const run = async (p, okText) => {
        try {
            const r = await p;
            if (ok(r)) { message.success(okText); onChanged(); }
        } catch (e) {
            toastErr(e, '操作失败');
        }
    };

    const inGroup = new Set((group.members || []).map((m) => m.node_id));
    return (
        <div style={{ padding: '4px 0' }}>
            <Table size="small" rowKey="node_id" pagination={false} dataSource={group.members || []} columns={[
                { title: '节点', key: 'n', render: (_, m) => `#${m.node_id} ${m.name}` },
                { title: 'IP', dataIndex: 'ip' },
                { title: '状态', dataIndex: 'status', render: (v) => <Tag>{v}</Tag> },
                {
                    title: '权重', dataIndex: 'weight', width: 140,
                    render: (v, m) => (
                        <InputNumber size="small" min={0} max={10000} defaultValue={v}
                            onBlur={(e) => { const w = Number(e.target.value); if (w !== v) run(tmAdminService.setGroupMember(group.id, m.node_id, w), '已更新'); }} />
                    ),
                },
                {
                    title: '', key: 'op', width: 70,
                    render: (_, m) => <Popconfirm title="移出分组？" onConfirm={() => run(tmAdminService.removeGroupMember(group.id, m.node_id), '已移出')}><Button type="link" size="small" danger>移出</Button></Popconfirm>,
                },
            ]} />
            <Space style={{ marginTop: 8 }}>
                <Select size="small" style={{ width: 220 }} placeholder="添加节点" value={nodeId} onChange={setNodeId}
                    options={nodes.filter((n) => !inGroup.has(n.id)).map((n) => ({ value: n.id, label: `#${n.id} ${n.name} ${n.ip}` }))} />
                <InputNumber size="small" min={0} max={10000} value={weight} onChange={(v) => setWeight(v ?? 100)} addonBefore="权重" style={{ width: 140 }} />
                <Button size="small" disabled={!nodeId} onClick={() => { run(tmAdminService.setGroupMember(group.id, nodeId, weight), '已添加'); setNodeId(null); }}>添加</Button>
            </Space>
        </div>
    );
};

const Groups = () => {
    const [rows, setRows] = useState([]);
    const [nodes, setNodes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [form] = Form.useForm();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [g, n] = await Promise.all([tmAdminService.listGroups(), tmAdminService.listNodes()]);
            setRows(Array.isArray(g?.data) ? g.data : []);
            setNodes(Array.isArray(n?.data) ? n.data : []);
        } catch (e) {
            toastErr(e, '加载失败');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const openEdit = (g) => {
        setEditing(g || {});
        form.setFieldsValue(g ? { name: g.name, code: g.code, smtp_host: g.smtp_host, description: g.description, status: g.status } : { status: 'active' });
    };

    const save = async () => {
        const v = await form.validateFields();
        setSaving(true);
        try {
            const r = editing?.id ? await tmAdminService.updateGroup(editing.id, v) : await tmAdminService.createGroup(v);
            if (ok(r)) { message.success('已保存'); setEditing(null); load(); }
        } catch (e) {
            toastErr(e, '保存失败');
        } finally {
            setSaving(false);
        }
    };

    const remove = async (g) => {
        try {
            const r = await tmAdminService.deleteGroup(g.id);
            if (ok(r)) { message.success('已删除'); load(); }
        } catch (e) {
            toastErr(e, '删除失败');
        }
    };

    const columns = [
        { title: 'ID', dataIndex: 'id', width: 60 },
        { title: '名称', dataIndex: 'name', render: (v, g) => <span>{v}{g.description ? <Text type="secondary" style={{ fontSize: 12 }}><br />{g.description}</Text> : null}</span> },
        { title: 'Code', dataIndex: 'code', render: (v) => <Text code>{v}</Text> },
        { title: 'SMTP 主机', dataIndex: 'smtp_host' },
        { title: '节点', key: 'members', width: 80, render: (_, g) => (g.members || []).length },
        { title: '覆盖流', dataIndex: 'streams_override', width: 80 },
        { title: '状态', dataIndex: 'status', width: 90, render: (v) => <Tag color={v === 'active' ? 'green' : 'default'}>{v}</Tag> },
        {
            title: '', key: 'op', width: 130,
            render: (_, g) => (
                <Space size={0}>
                    <Button type="link" size="small" onClick={() => openEdit(g)}>编辑</Button>
                    {g.code !== 'default' && (
                        <Popconfirm title="删除分组？" okButtonProps={{ danger: true }} onConfirm={() => remove(g)}><Button type="link" size="small" danger>删除</Button></Popconfirm>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <Card title="服务分组" extra={<Space><Button icon={<ReloadOutlined />} onClick={load} /><Button type="primary" icon={<PlusOutlined />} onClick={() => openEdit(null)}>新建分组</Button></Space>}>
            <Table rowKey="id" size="small" loading={loading} dataSource={rows} columns={columns} pagination={false}
                expandable={{ expandedRowRender: (g) => <Members group={g} nodes={nodes} onChanged={load} /> }} />
            <Modal open={!!editing} title={editing?.id ? `编辑分组 #${editing.id}` : '新建分组'} onOk={save} confirmLoading={saving} onCancel={() => setEditing(null)} destroyOnClose>
                <Form form={form} layout="vertical">
                    <Form.Item name="name" label="名称" rules={[{ required: true }]}><Input /></Form.Item>
                    <Form.Item name="code" label="Code" rules={[{ required: true }, { pattern: /^[a-z0-9_-]{2,32}$/, message: '2-32 位小写字母、数字、-_' }]}>
                        <Input disabled={editing?.code === 'default'} placeholder="vip" />
                    </Form.Item>
                    <Form.Item name="smtp_host" label="SMTP 主机" rules={[{ required: true }]}><Input placeholder="smtp-vip.tm.yeapcloud.net" /></Form.Item>
                    <Form.Item name="description" label="说明"><Input /></Form.Item>
                    <Form.Item name="status" label="状态"><Select options={[{ value: 'active', label: 'active' }, { value: 'inactive', label: 'inactive' }]} /></Form.Item>
                </Form>
            </Modal>
        </Card>
    );
};

export default Groups;
